import { ThemeEvents, CartUpdatedEvent } from '@theme/events';
import { processGifts, extractCartItemCollections } from '@theme/auto-gift-processor';

class CartDrawer extends HTMLElement {
  #rendering = false;

  constructor() {
    super();
    this.checkoutButton = null;
    this.openCartIcons = null;

    this.backdropClickHandler = this.#onBackdropClick.bind(this);
    this.checkoutButtonClickHandler = this.#handleCheckoutButtonClick.bind(this);
    this.cartUpdateHandler = this.#handleCartUpdate.bind(this);
    this.openHandler = this.open.bind(this);
    this.closeHandler = this.close.bind(this);
    this.cartObserver = null;
    this.cartObserverTimer = null;
  }

  connectedCallback() {
    this.checkoutButton = this.querySelector('[data-ref="checkout-button"]');

    this.addEventListener('click', this.backdropClickHandler);

    this.checkoutButton.addEventListener('click', this.checkoutButtonClickHandler);

    document.addEventListener(ThemeEvents.cartUpdated, this.cartUpdateHandler);

    document.addEventListener(ThemeEvents.cartOpen, this.openHandler);
    document.addEventListener(ThemeEvents.cartClose, this.closeHandler);
    const content = this.querySelector('.cart-drawer__inner');
    if (content) {
      this.cartObserver = new MutationObserver(() => {
        clearTimeout(this.cartObserverTimer);
        this.cartObserverTimer = setTimeout(() => document.querySelector('cart-icon')?.syncCartCountFromServer?.(), 200);
      });
      this.cartObserver.observe(content, { childList: true, subtree: true });
    }

    // Specific logic for Shopify Theme Editor
    if (Shopify.designMode) {
      document.addEventListener('shopify:section:unload', this.#onSectionUnload);
      if (window.theme?.isCartDrawerOpen) {
        this.open();
        window.theme.isCartDrawerOpen = false; // Reset the flag
      }
    }
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartClose, this.closeHandler);
    this.cartObserver?.disconnect();
    clearTimeout(this.cartObserverTimer);
    document.removeEventListener(ThemeEvents.cartOpen, this.openHandler);

    document.removeEventListener(ThemeEvents.cartUpdated, this.cartUpdateHandler);

    this.checkoutButton.removeEventListener('click', this.checkoutButtonClickHandler);

    this.removeEventListener('click', this.backdropClickHandler);

    // Specific logic for Shopify Theme Editor
    if (Shopify.designMode) {
      document.removeEventListener('shopify:section:unload', this.#onSectionUnload);
    }
  }

  #onSectionUnload = (event) => {
    if (event.detail.sectionId !== this.dataset.sectionId) return;

    window.theme = window.theme || {};
    window.theme.isCartDrawerOpen = this.isOpen();
  };

  #handleCheckoutButtonClick(event) {
    event.target.classList.add('is-loading');
  }

  #handleCartUpdate(event) {
    this.renderCartContents(event.detail.data);
  }

  #onBackdropClick(event) {
    if (event.target.closest('dialog[open]')) return;

    const rect = this.querySelector('.cart-drawer__inner').getBoundingClientRect();
    const isInDialog = rect.top <= event.clientY && event.clientY <= rect.top + rect.height && rect.left <= event.clientX && event.clientX <= rect.left + rect.width;

    if (!isInDialog) {
      this.close();
    }
  }

  open() {
    document.querySelector('cart-icon')?.syncCartCountFromServer?.();
    // Nettoyer les classes pour éviter les conflits
    this.classList.remove('is-closing');
    this.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
  }

  close() {
    // Ajouter la classe de fermeture pour déclencher l'animation
    this.classList.add('is-closing');

    // Attendre la fin de l'animation avant de masquer complètement
    setTimeout(() => {
      this.classList.remove('is-open', 'is-closing');
      document.body.classList.remove('overflow-hidden');
    }, 125); // Durée légèrement inférieure à --animation-speed pour éviter les décalages
  }

  isOpen() {
    return this.classList.contains('is-open');
  }

  #updateAutoGiftsMapping(autoGiftsHtml) {
    if (!autoGiftsHtml) return;

    const parsed = new DOMParser().parseFromString(autoGiftsHtml, 'text/html');
    const newScript = parsed.querySelector('[data-ref="cart-item-collections"]');
    const currentScript = document.querySelector('[data-ref="cart-item-collections"]');
    if (newScript && currentScript) currentScript.textContent = newScript.textContent;
  }

  async renderCartContents(parsedState) {
    if (this.#rendering) return;
    this.#rendering = true;

    try {
      const sectionId = this.dataset.sectionId;
      const newCartDrawer = new DOMParser().parseFromString(parsedState.sections[sectionId], 'text/html');

      const newAutoGiftsHtml = parsedState.sections?.['auto-gifts'];
      const newAutoGifts = newAutoGiftsHtml ? new DOMParser().parseFromString(newAutoGiftsHtml, 'text/html') : null;
      const newCollections = newAutoGifts ? extractCartItemCollections(newAutoGifts) : {};

      const giftResult = await processGifts(sectionId, newCollections);

      if (giftResult?.sections) {
        parsedState = { ...parsedState, sections: { ...parsedState.sections, ...giftResult.sections }, itemCount: giftResult.itemCount ?? parsedState.itemCount };
      }

      const finalCartDrawer = giftResult?.sections?.[sectionId]
        ? new DOMParser().parseFromString(giftResult.sections[sectionId], 'text/html')
        : newCartDrawer;

      const currentCartDrawer = this.querySelector('.cart-drawer__inner');
      currentCartDrawer.innerHTML = finalCartDrawer.querySelector('.cart-drawer__inner').innerHTML;

      this.#updateAutoGiftsMapping(parsedState.sections?.['auto-gifts']);

      setTimeout(() => {
        if (parsedState.itemCount == 0) {
          this.classList.add('cart-drawer--empty');
        } else {
          this.classList.remove('cart-drawer--empty');
        }
      });

      this.#syncCartIconCount(parsedState);
      // The gift processor may change cart lines after the original mutation.
      // Read the final server state after rendering, rather than trusting
      // the earlier event's itemCount (which may still be 3 when cart is empty).
      await this.#syncFinalCartCount();
    } finally {
      this.#rendering = false;
    }
  }

  async #syncFinalCartCount() {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(root + 'cart.js?_edukid_final=' + Date.now(), {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!response.ok) return;
      const cart = await response.json();
      if (!Number.isInteger(cart.item_count)) return;
      document.querySelectorAll('cart-icon').forEach(icon => icon.setCount?.(cart.item_count));
      this.classList.toggle('cart-drawer--empty', cart.item_count === 0);
    } catch (error) {
      console.warn('Final cart count reconciliation failed:', error);
    }
  }

  #syncCartIconCount(parsedState) {
    const cartIcon = document.querySelector('cart-icon');
    if (!cartIcon) return;

    const countEl = cartIcon.querySelector('[data-ref="cart-count"]');
    if (!countEl) return;

    if (typeof parsedState.itemCount === 'number') {
      if (typeof cartIcon.setCount === 'function') cartIcon.setCount(parsedState.itemCount);
      else countEl.textContent = parsedState.itemCount;
    }

    // Refresh from the authoritative Shopify cart, including async gift changes.
    cartIcon.ensureCartCountIsCorrect();
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItem extends HTMLElement {
  constructor() {
    super();

    this.removeItemButton = null;
    this.quantitySelectorInput = null;
    this.itemRemoveHandler = this.#handleItemRemove.bind(this);
    this.quantityInputChangeHandler = this.#handleQuantityInputChange.bind(this);
  }

  connectedCallback() {
    this.removeItemButton = this.querySelector('[data-ref="remove-item"]');
    this.quantitySelectorInput = this.querySelector('[data-ref="quantity-selector-input"]');

    this.removeItemButton?.addEventListener('click', this.itemRemoveHandler);
    this.quantitySelectorInput?.addEventListener('change', this.quantityInputChangeHandler);
  }

  disconnectedCallback() {
    this.quantitySelectorInput?.removeEventListener('change', this.quantityInputChangeHandler);
    this.removeItemButton?.removeEventListener('click', this.itemRemoveHandler);
  }

  #handleItemRemove(event) {
    event.preventDefault();
    this.#updateLineItemQuantity(this.dataset.index, 0);
  }

  #handleQuantityInputChange(event) {
    event.preventDefault();
    this.#updateLineItemQuantity(this.dataset.index, this.quantitySelectorInput.value);
  }

  async #updateLineItemQuantity(line, quantity) {
    if (this.classList.contains('cart-drawer__item--loading')) return;
    this.#enableLoading();

    const drawer = this.closest('cart-drawer');
    const sectionId = drawer?.dataset.sectionId;
    const sections = [sectionId, 'auto-gifts'].filter(Boolean);

    try {
      // A Shopify cart line key stays specific to this item even if its index
      // changes when other items are removed or a discount is recalculated.
      const response = await fetch(Theme.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: this.dataset.lineKey || undefined,
          ...(!this.dataset.lineKey ? { line: Number(line) } : {}),
          quantity: Number(quantity),
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();

      if (!response.ok || result.errors || result.status >= 400) {
        const message = typeof result.description === 'string'
          ? result.description
          : typeof result.message === 'string'
            ? result.message
            : 'Der Warenkorb konnte nicht aktualisiert werden.';
        throw new Error(message);
      }

      // Reflect the authoritative cart/change.js item count before rendering the drawer.
      if (Number.isInteger(result.item_count)) {
        document.querySelectorAll('cart-icon').forEach(icon => icon.setCount?.(result.item_count));
      }
      // Shopify can return null sections after an otherwise successful change.
      // Fetch fresh sections so the drawer and its badge always reflect the cart.
      let refreshedSections = result.sections;
      if (sectionId && !refreshedSections?.[sectionId]) {
        const root = window.Shopify?.routes?.root || '/';
        const sectionResponse = await fetch(
          root + '?sections=' + encodeURIComponent(sections.join(',')),
          { headers: { Accept: 'application/json' } },
        );
        if (!sectionResponse.ok) throw new Error('Der Warenkorb konnte nicht neu geladen werden.');
        refreshedSections = await sectionResponse.json();
      }

      document.dispatchEvent(new CartUpdatedEvent({}, {
        itemCount: result.item_count,
        source: 'cart-drawer-item',
        sections: refreshedSections,
      }));
    } catch (error) {
      console.error('Cart drawer update failed:', error);
      document.dispatchEvent(new CustomEvent('toast:open', {
        detail: { type: 'error', message: error.message || 'Der Warenkorb konnte nicht aktualisiert werden.' },
      }));
    } finally {
      this.#disableLoading();
    }
  }

  #enableLoading() {
    this.classList.add('cart-drawer__item--loading');
  }

  #disableLoading() {
    this.classList.remove('cart-drawer__item--loading');
  }
}

if (!customElements.get('cart-drawer-item')) {
  customElements.define('cart-drawer-item', CartDrawerItem);
}
